import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, X, Plus, Check, Search } from "lucide-react";
import { useApp } from "@/context/AppContext";

/**
 * Reusable type-to-search dropdown with keyboard nav and an optional "+ Create New" action.
 *
 * Props:
 *   value: string | null (selected id)
 *   onChange(id, item)
 *   options: [{ id, label, sublabel? }]
 *   placeholder
 *   allowCreate: bool
 *   onCreate(query): fires when user picks "+ Create New" (query = current search text)
 *   createLabel: string ("Create new item")
 *   testid
 */
export default function SearchableSelect({
  value, onChange, options = [], placeholder = "Select…",
  allowCreate = false, onCreate, createLabel, testid, disabled = false,
}) {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = q
    ? options.filter((o) => (o.label + " " + (o.sublabel || "")).toLowerCase().includes(q.toLowerCase()))
    : options;

  const selected = options.find((o) => o.id === value);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);
  useEffect(() => { setActive(0); }, [q, open]);

  const totalRows = filtered.length + (allowCreate && q ? 1 : 0);

  const pick = (o) => { onChange(o.id, o); setOpen(false); setQ(""); };
  const doCreate = () => { setOpen(false); const query = q; setQ(""); onCreate && onCreate(query); };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, totalRows - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (active < filtered.length) pick(filtered[active]);
      else if (allowCreate && q) doCreate();
    }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        data-testid={testid ? `${testid}-trigger` : undefined}
        className="w-full h-12 rounded-lg border border-stone-300 px-3 text-left text-sm flex items-center justify-between bg-white disabled:opacity-50"
      >
        <span className={selected ? "text-stone-900 truncate" : "text-stone-400 truncate"}>
          {selected ? selected.label : placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {selected && !disabled && (
            <span
              onClick={(e) => { e.stopPropagation(); onChange(null, null); }}
              data-testid={testid ? `${testid}-clear` : undefined}
              className="h-6 w-6 rounded grid place-items-center hover:bg-stone-100 cursor-pointer"
            ><X className="w-3.5 h-3.5 text-stone-500" /></span>
          )}
          <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg border border-stone-200 shadow-lg overflow-hidden">
          <div className="p-2 border-b border-stone-100 flex items-center gap-2">
            <Search className="w-4 h-4 text-stone-400 shrink-0" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKey}
              placeholder={t("search")}
              data-testid={testid ? `${testid}-search` : undefined}
              className="flex-1 outline-none text-sm bg-transparent h-8"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1" data-testid={testid ? `${testid}-options` : undefined}>
            {filtered.length === 0 && (
              <div className="p-3 text-xs text-stone-400 text-center">{t("no_results") || "No results"}</div>
            )}
            {filtered.map((o, i) => (
              <button
                key={o.id}
                type="button"
                onClick={() => pick(o)}
                onMouseEnter={() => setActive(i)}
                data-testid={testid ? `${testid}-opt-${o.id}` : undefined}
                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 text-sm ${i === active ? "bg-[#FFF7ED]" : "hover:bg-stone-50"}`}
              >
                <div className="min-w-0">
                  <div className="truncate font-medium text-stone-900">{o.label}</div>
                  {o.sublabel && <div className="text-xs text-stone-500 truncate font-gujarati">{o.sublabel}</div>}
                </div>
                {o.id === value && <Check className="w-4 h-4 text-[#B45309] shrink-0" />}
              </button>
            ))}
            {allowCreate && q && (
              <button
                type="button"
                onClick={doCreate}
                onMouseEnter={() => setActive(filtered.length)}
                data-testid={testid ? `${testid}-create` : undefined}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm border-t border-stone-100 text-[#B45309] font-semibold ${active === filtered.length ? "bg-[#FFF7ED]" : "hover:bg-stone-50"}`}
              >
                <Plus className="w-4 h-4" /> {createLabel || t("create_new_item")} “{q}”
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
